import React, { useEffect, useState } from "react";
import Layout from "../components/Layout";
import SEOHead from "../components/SEOHead";
import PageHeader from "../components/PageHeader";
import KPI from "../components/KPI";
import DemoBadge from "../components/DemoBadge";
import { budgetApi, recApi, dashApi } from "../lib/api";
import { RadialBarChart, RadialBar, ResponsiveContainer, PieChart, Pie, Cell, Tooltip, Legend } from "recharts";

const SEV_COLORS = { low: "#2F855A", medium: "#ECC94B", high: "#E53E3E", critical: "#7F1D1D" };

const InfrastructureRisk = () => {
  const [summary, setSummary] = useState(null);
  const [projects, setProjects] = useState([]);
  const [recs, setRecs] = useState([]);

  useEffect(() => {
    dashApi.summary().then(setSummary).catch(() => {});
    budgetApi.projects("City of Cedar Falls").then(d => setProjects(d.capital_projects || []));
    recApi.list("City of Cedar Falls").then(d => setRecs(d.recommendations || []));
  }, []);

  const sev = summary?.risk_flags_by_severity || { low: 0, medium: 0, high: 0, critical: 0 };
  const pieData = Object.entries(sev).map(([k, v]) => ({ name: k, value: v, fill: SEV_COLORS[k] }));

  return (
    <Layout>
      <SEOHead title="Infrastructure Risk Dashboard | AuditIQ" path="/infrastructure"
        description="AuditIQ infrastructure risk dashboard: capital project escalation, deferred maintenance, reallocation recommendations." />
      <PageHeader eyebrow="Infrastructure" demo
        title="Infrastructure Risk Dashboard"
        subtitle="Capital projects, maintenance backlogs, and reallocation recommendations — framed as decision-support for public policy review." />

      <section className="max-w-[1400px] mx-auto px-6 py-14 grid md:grid-cols-4 gap-5">
        <KPI label="High Severity Flags" value={sev.high} accent="critical" testid="infra-kpi-high" />
        <KPI label="Medium Severity" value={sev.medium} accent="warning" testid="infra-kpi-med" />
        <KPI label="Low / Noted" value={sev.low} accent="success" testid="infra-kpi-low" />
        <KPI label="Transparency Avg" value={`${summary?.public_transparency_score_avg ?? "—"}`} testid="infra-kpi-tr" sublabel="out of 100" />
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-14 grid lg:grid-cols-[1fr_1fr] gap-6">
        <div className="panel-elev p-6">
          <div className="flex items-center justify-between mb-3"><h3 className="font-display text-xl">Risk Distribution</h3><DemoBadge /></div>
          <div className="h-72">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={pieData} dataKey="value" nameKey="name" innerRadius={60} outerRadius={100} paddingAngle={4}>
                  {pieData.map((e, i) => <Cell key={i} fill={e.fill} />)}
                </Pie>
                <Tooltip contentStyle={{ background: "#152A52", border: "1px solid #C9A84C", color: "#F4EFE6" }} />
                <Legend wrapperStyle={{ color: "#D8D2C5" }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="panel-elev p-6">
          <div className="flex items-center justify-between mb-3"><h3 className="font-display text-xl">Capital Project Escalation</h3><DemoBadge /></div>
          <ul className="space-y-3">
            {projects.map(p => (
              <li key={p.id} className="border-b border-gold-dim/15 pb-3">
                <div className="flex items-center justify-between">
                  <div className="text-cream">{p.name}</div>
                  <div className={`text-xs font-semibold ${p.escalation_pct > 20 ? "text-[color:var(--auditiq-critical)]" : p.escalation_pct > 5 ? "text-[color:var(--auditiq-warning)]" : "text-[color:var(--auditiq-success)]"}`}>
                    {p.escalation_pct > 0 ? "+" : ""}{p.escalation_pct.toFixed(1)}%
                  </div>
                </div>
                <div className="text-xs text-cream-dim mt-1">
                  {p.category} · ${Math.round(p.original_budget / 1e3)}k → ${Math.round(p.current_budget / 1e3)}k · {p.status}
                </div>
              </li>
            ))}
          </ul>
        </div>
      </section>

      <section className="max-w-[1400px] mx-auto px-6 pb-20">
        <div className="flex items-center justify-between mb-4">
          <h3 className="font-display text-2xl">Reallocation Recommendations</h3>
          <DemoBadge />
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-5">
          {recs.map(r => (
            <div key={r.id} className="panel-elev p-6" data-testid={`rec-${r.id}`}>
              <div className="text-[10px] uppercase tracking-[0.22em] text-gold mb-2">{r.language}</div>
              <div className="font-display text-lg text-cream leading-snug">{r.title}</div>
              <div className="text-xs text-cream-dim mt-2">
                From <span className="text-gold">{r.from_category}</span> → to <span className="text-gold">{r.to_category}</span>
              </div>
              <div className="metric text-2xl text-gold mt-3">${(r.suggested_amount_usd/1e3).toFixed(0)}k</div>
              <div className="gold-rule my-4" />
              <div className="text-xs text-steel uppercase tracking-wider">Community impact</div>
              <div className="text-sm text-cream-dim mt-1">{r.community_impact}</div>
              <div className="text-xs text-steel uppercase tracking-wider mt-4">Questions to ask</div>
              <ul className="text-sm text-cream-dim mt-1 list-disc pl-5 space-y-1">
                {r.resident_questions?.slice(0, 3).map((q, i) => <li key={i}>{q}</li>)}
              </ul>
            </div>
          ))}
        </div>
      </section>
    </Layout>
  );
};

export default InfrastructureRisk;
